import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiHome, FiUsers, FiDollarSign, FiFileText,
  FiPlus, FiEdit3, FiTrash2, FiMail,
  FiPhone, FiMapPin, FiCamera, FiBriefcase, FiCheck, FiShield, FiDroplet, FiWifiOff
} from 'react-icons/fi';
import { propertiesService, Property } from '../services/properties.service';
import { documentsService } from '../services/documents.service';
import { api } from '../config/api';
import { toast } from 'react-hot-toast';
import { tw, monoFont } from '../utils/terminalTheme';

interface PropertyWithStats extends Property {
  documentCount?: number;
  imageCount?: number;
  monthlyRent?: number;
}

const CoxLogo = () => (
  <svg width="28" height="14" viewBox="0 0 100 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-60 group-hover:opacity-100 transition-opacity">
    <text x="0" y="40" fontFamily="Arial Black, Arial" fontWeight="900" fontSize="42" fill="#3B82F6" letterSpacing="-2">COX</text>
  </svg>
);


const RepublicLogo = () => (
  <svg width="16" height="16" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0">
    <circle cx="50" cy="50" r="48" fill="#16a34a"/>
    <text x="50" y="68" fontFamily="Arial Black, Arial" fontWeight="900" fontSize="56" fill="white" textAnchor="middle">R</text>
  </svg>
);

const Properties: React.FC = () => {
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rachioStatus, setRachioStatus] = useState<Record<string, any>>({});
  const navigate = useNavigate();

  useEffect(() => {
    loadProperties();
    loadRachioStatus();
  }, []);

  const loadRachioStatus = async () => {
    try {
      const response = await api.get('/api/v1/rachio/status');
      setRachioStatus(response.data);
    } catch {
      // Rachio unavailable - non-critical
    }
  };

  const loadProperties = async () => {
    try {
      setIsLoading(true);
      const data = await propertiesService.getProperties();

      const enhancedProperties = await Promise.all(
        data.map(async (property) => {
          try {
            const docs = await documentsService.getDocuments({
              property_id: property.id
            });

            let imageCount = 0;
            try {
              const messagesResponse = await api.get(`/messages/?property_id=${property.id}&has_media=true`);
              if (messagesResponse.data && Array.isArray(messagesResponse.data)) {
                imageCount = messagesResponse.data.reduce((total: number, msg: any) => {
                  return total + (msg.media_urls?.length || 0);
                }, 0);
              }
            } catch (err) {
              console.error('Error fetching image count:', err);
            }

            return {
              ...property,
              documentCount: docs?.length || 0,
              imageCount: imageCount,
              monthlyRent: property.monthly_rent || 0
            };
          } catch (error) {
            return property;
          }
        })
      );

      const sortedProperties = enhancedProperties.sort((a, b) => {
        const numA = parseInt(a.address?.match(/^\d+/)?.[0] || '0');
        const numB = parseInt(b.address?.match(/^\d+/)?.[0] || '0');
        if (numA !== numB) return numB - numA;
        return (a.address || '').localeCompare(b.address || '');
      });

      setProperties(sortedProperties);
    } catch (error) {
      toast.error('Failed to load properties');
      console.error('Error loading properties:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    try {
      await propertiesService.deleteProperty(id);
      toast.success('Property deleted successfully');
      loadProperties();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  if (isLoading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  const occupied = properties.filter(p => p.occupancy_status === 'occupied' || p.occupancy_status === 'partially_occupied').length;
  const totalRevenue = properties.reduce((sum, p) => sum + (p.monthlyRent || 0), 0);
  const totalValue = properties.reduce((sum, p) => sum + (p.estimated_value || 0), 0);
  const totalMortgage = properties.reduce((sum, p) => sum + (p.mortgage_balance || 0), 0);
  const totalDocs = properties.reduce((sum, p) => sum + (p.documentCount || 0), 0);
  const totalImages = properties.reduce((sum, p) => sum + (p.imageCount || 0), 0);

  const getTrashStatus = (trashDay: string | null) => {
    if (!trashDay) return null;
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todayIdx = new Date().getDay();
    const todayName = days[todayIdx];
    const tomorrowName = days[(todayIdx + 1) % 7];
    if (trashDay === todayName) return 'today';
    if (trashDay === tomorrowName) return 'tomorrow';
    return null;
  };

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
              <span className="text-cyan-400">~/</span>properties
              <span className="text-gray-600 text-sm font-normal ml-3">
                <span className="text-green-500">$</span> ls -la
              </span>
            </h1>
            <p className="text-gray-500 text-sm font-mono mt-1">{properties.length} properties managed</p>
          </div>
          <button
            onClick={() => navigate('/properties/new')}
            className={tw.btnPrimary + ' flex items-center gap-2'}
          >
            <FiPlus className="w-4 h-4" />
            Add Property
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
        <div className={tw.statCard}>
          <div className="flex items-center justify-between">
            <div>
              <p className={tw.statLabel}>Properties</p>
              <p className={tw.statValueCyan + ' text-xl'}>{properties.length}</p>
            </div>
            <FiHome className="w-5 h-5 text-cyan-400 opacity-50" />
          </div>
        </div>

        <div className={tw.statCard}>
          <div className="flex items-center justify-between">
            <div>
              <p className={tw.statLabel}>Occupied</p>
              <p className="text-xl font-bold font-mono text-green-500">{occupied}</p>
            </div>
            <FiUsers className="w-5 h-5 text-green-500 opacity-50" />
          </div>
        </div>

        <div className={tw.statCard}>
          <div className="flex items-center justify-between">
            <div>
              <p className={tw.statLabel}>Revenue</p>
              <p className="text-xl font-bold font-mono text-green-500">${totalRevenue.toLocaleString()}</p>
            </div>
            <FiDollarSign className="w-5 h-5 text-green-500 opacity-50" />
          </div>
        </div>

        <div className={tw.statCard}>
          <div className="flex items-center justify-between">
            <div>
              <p className={tw.statLabel}>Value</p>
              <p className="text-xl font-bold font-mono text-purple-400">${totalValue.toLocaleString()}</p>
            </div>
            <FiHome className="w-5 h-5 text-purple-400 opacity-50" />
          </div>
        </div>

        <div className={tw.statCard}>
          <div className="flex items-center justify-between">
            <div>
              <p className={tw.statLabel}>Mortgage</p>
              <p className="text-sm font-bold font-mono text-orange-400 leading-tight">${totalMortgage.toLocaleString()}</p>
            </div>
            <FiBriefcase className="w-5 h-5 text-orange-400 opacity-50" />
          </div>
        </div>

        <div className={tw.statCard}>
          <div className="flex items-center justify-between">
            <div>
              <p className={tw.statLabel}>Documents</p>
              <p className="text-xl font-bold font-mono text-blue-400">{totalDocs}</p>
            </div>
            <FiFileText className="w-5 h-5 text-blue-400 opacity-50" />
          </div>
        </div>

        <div className={tw.statCard}>
          <div className="flex items-center justify-between">
            <div>
              <p className={tw.statLabel}>Images</p>
              <p className="text-xl font-bold font-mono text-purple-400">{totalImages}</p>
            </div>
            <FiCamera className="w-5 h-5 text-purple-400 opacity-50" />
          </div>
        </div>
      </div>

      {/* Properties Grid */}
      {properties.length === 0 ? (
        <div className={tw.statCard + ' text-center py-12'}>
          <FiHome className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <h3 className="text-lg font-mono text-white mb-2">No Properties Yet</h3>
          <p className="text-gray-500 font-mono text-sm mb-6">Add your first property to get started</p>
          <button
            onClick={() => navigate('/properties/new')}
            className={tw.btnPrimary}
          >
            Add Your First Property
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map((property) => (
            <div
              key={property.id}
              className="bg-gray-800/80 border border-gray-700/50 rounded-lg overflow-hidden hover:border-cyan-500/30 transition-all cursor-pointer group"
              onClick={() => navigate(`/properties/${property.id}`)}
            >
              <div className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-mono font-semibold text-white truncate">
                      {property.name || 'Unnamed Property'}
                    </h3>
                    <p className="text-gray-500 text-xs font-mono flex items-center mt-1">
                      <FiMapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{property.address || 'No address'}</span>
                    </p>
                  </div>
                  {(property as any).cox_account && (
                    <div className="flex items-center mr-1" title={`Cox: ${(property as any).cox_account}${(property as any).cox_monthly ? " - $" + (property as any).cox_monthly + "/mo" : ""}`}>
                      <CoxLogo />
                    </div>
                  )}
                  {(property as any).trash_day && (
                    <div className="flex items-center mr-1" title={`Trash: ${(property as any).trash_day}s`}>
                      <RepublicLogo />
                    </div>
                  )}
                  {(property as any).has_cameras && (
                    <div className="flex items-center mr-1" title="Security cameras monitored">
                      <FiCamera className="w-3.5 h-3.5 text-cyan-500 opacity-70 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                  {(property as any).has_irrigation && (() => {
                    const rs = rachioStatus[String(property.id)];
                    const color = !rs ? 'text-gray-500' : rs.status === 'offline' ? 'text-red-400' : rs.status === 'watering' ? 'text-blue-400' : rs.status === 'watered_today' ? 'text-emerald-400' : 'text-teal-600';
                    const title = !rs ? 'Rachio irrigation' : rs.status === 'offline' ? 'Irrigation offline!' : rs.status === 'watering' ? 'Currently watering' : rs.status === 'watered_today' ? `Watered ${rs.hours_ago}h ago` : rs.status === 'watered_recently' ? `Watered ${Math.round(rs.hours_ago / 24)}d ago` : 'Irrigation online';
                    return (
                      <div className="flex items-center mr-1" title={title}>
                        {rs?.status === 'offline' ? <FiWifiOff className={`w-3.5 h-3.5 ${color} opacity-80`} /> : <FiDroplet className={`w-3.5 h-3.5 ${color} opacity-80 group-hover:opacity-100 transition-opacity`} />}
                      </div>
                    );
                  })()}
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/properties/${property.id}/edit`); }}
                      className="p-1.5 text-gray-500 hover:text-cyan-400 hover:bg-gray-700/50 rounded transition-colors"
                      title="Edit Property"
                    >
                      <FiEdit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(property.id); }}
                      className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-gray-700/50 rounded transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Property Details */}
                <div className="space-y-2">
                  {property.occupancy_status === 'occupied' ? (
                    <div className="flex items-center text-xs font-mono">
                      <FiUsers className="w-3.5 h-3.5 text-green-500 mr-2" />
                      <span className="text-gray-300">{property.tenant_name || 'Occupied'}</span>
                    </div>
                  ) : property.occupancy_status === 'partially_occupied' ? (
                    <div className="flex items-center text-xs font-mono">
                      <FiUsers className="w-3.5 h-3.5 text-amber-400 mr-2" />
                      <span className="text-gray-300">Partially Occupied</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-xs font-mono">
                      <FiUsers className="w-3.5 h-3.5 text-gray-600 mr-2" />
                      <span className="text-gray-600">Vacant</span>
                    </div>
                  )}

                  {property.monthlyRent && property.monthlyRent > 0 && (
                    <div className="flex items-center text-xs font-mono">
                      <FiDollarSign className="w-3.5 h-3.5 text-green-500 mr-2" />
                      <span className="text-green-500">${property.monthlyRent.toLocaleString()}/mo</span>
                    </div>
                  )}

                  <div className="flex items-center text-xs font-mono">
                    <FiFileText className="w-3.5 h-3.5 text-blue-400 mr-2" />
                    <span className="text-gray-400">{property.documentCount || 0} docs, {property.imageCount || 0} images</span>
                  </div>

                  {property.estimated_value && (
                    <div className="flex items-center text-xs font-mono">
                      <FiHome className="w-3.5 h-3.5 text-purple-400 mr-2" />
                      <span className="text-gray-400">Value: <span className="text-purple-400">${property.estimated_value.toLocaleString()}</span></span>
                    </div>
                  )}

                  {property.estimated_rent && (
                    <div className="flex items-center text-xs font-mono">
                      <FiDollarSign className="w-3.5 h-3.5 text-amber-400 mr-2" />
                      <span className="text-gray-400">Est. Rent: <span className="text-amber-400">${property.estimated_rent.toLocaleString()}/mo</span></span>
                    </div>
                  )}
                </div>

                {/* Mortgage Info */}
                {property.mortgage_lender && (
                  <div className="mt-3 pt-3 border-t border-gray-700/50">
                    <div className="flex items-center text-xs font-mono mb-1.5">
                      <FiBriefcase className="w-3.5 h-3.5 text-orange-400 mr-2" />
                      <span className="text-orange-400 font-medium">{property.mortgage_lender}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 text-[11px] text-gray-500 font-mono ml-5">
                      {property.mortgage_balance && (
                        <div>Bal: <span className="text-gray-300">${property.mortgage_balance.toLocaleString()}</span></div>
                      )}
                      {property.mortgage_payment && (
                        <div>Pmt: <span className="text-gray-300">${property.mortgage_payment.toLocaleString()}/mo</span></div>
                      )}
                      {property.mortgage_rate && (
                        <div>Rate: <span className="text-gray-300">{property.mortgage_rate}%</span></div>
                      )}
                      {property.mortgage_maturity_date && (
                        <div>Mat: <span className="text-gray-300">{new Date(property.mortgage_maturity_date).toLocaleDateString()}</span></div>
                      )}
                    </div>
                  </div>
                )}

                {property.ownership_type === 'owned_outright' && !property.mortgage_lender && (
                  <div className="flex items-center text-xs font-mono mt-3 pt-3 border-t border-gray-700/50">
                    <FiCheck className="w-3.5 h-3.5 text-green-500 mr-2" />
                    <span className="text-green-500">Owned Outright</span>
                  </div>
                )}

                {/* HOA Info */}
                {property.hoa_company_name && (
                  <div className="mt-3 pt-3 border-t border-gray-700/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs font-mono">
                        <FiShield className="w-3.5 h-3.5 text-cyan-400 mr-2" />
                        <span className="text-cyan-400 font-medium truncate" style={{maxWidth: '140px'}}>{property.hoa_company_name}</span>
                      </div>
                      {property.hoa_monthly_fee && (
                        <span className="text-green-500 text-xs font-mono font-semibold">${property.hoa_monthly_fee}/mo</span>
                      )}
                    </div>
                    {property.hoa_account_number && (
                      <div className="text-gray-600 text-[10px] font-mono mt-1 ml-5">{property.hoa_account_number}</div>
                    )}
                  </div>
                )}

                {/* Tenant Contact */}
                {property.occupancy_status === 'occupied' && (property.tenant_email || property.tenant_phone) && (
                  <div className="mt-3 pt-3 border-t border-gray-700/50">
                    <div className="flex justify-end gap-3">
                      {property.tenant_email && (
                        <a
                          href={`mailto:${property.tenant_email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-500 hover:text-cyan-400 transition-colors"
                          title="Email Tenant"
                        >
                          <FiMail className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {property.tenant_phone && (
                        <a
                          href={`tel:${property.tenant_phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-500 hover:text-cyan-400 transition-colors"
                          title="Call Tenant"
                        >
                          <FiPhone className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
                {/* Irrigation Status */}
                {(property as any).has_irrigation && rachioStatus[String(property.id)] && (() => {
                  const rs = rachioStatus[String(property.id)];
                  if (rs.status === 'offline') {
                    return (
                      <div className="mt-3 pt-3 border-t border-red-900/50">
                        <div className="flex items-center gap-2 text-xs font-mono text-red-400">
                          <FiWifiOff className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="flex-1 font-semibold">Irrigation offline</span>
                          <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/40 text-red-300 rounded-full text-[10px] font-bold">CHECK</span>
                        </div>
                      </div>
                    );
                  }
                  if (rs.status === 'watering') {
                    return (
                      <div className="mt-3 pt-3 border-t border-blue-800/50">
                        <div className="flex items-center gap-2 text-xs font-mono text-blue-400">
                          <FiDroplet className="w-3.5 h-3.5 flex-shrink-0 animate-pulse" />
                          <span className="flex-1">Irrigation</span>
                          <span className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/40 text-blue-300 rounded-full text-[10px] font-bold">WATERING NOW</span>
                        </div>
                      </div>
                    );
                  }
                  if (rs.status === 'watered_today') {
                    return (
                      <div className="mt-3 pt-3 border-t border-gray-700/50">
                        <div className="flex items-center gap-2 text-xs font-mono text-emerald-400">
                          <FiDroplet className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="flex-1">Irrigation</span>
                          <span className="text-emerald-500 font-semibold">{rs.hours_ago}h ago</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Trash Schedule */}
                {(property as any).trash_day && (() => {
                  const status = getTrashStatus((property as any).trash_day);
                  const bulkSameDay = (property as any).bulk_day === (property as any).trash_day;
                  const isAlert = status === 'today' || status === 'tomorrow';
                  return (
                    <div className={`mt-3 pt-3 border-t ${isAlert ? 'border-gray-600/80' : 'border-gray-700/50'}`}>
                      <div className={`flex items-center gap-2 text-xs font-mono ${status === 'today' ? 'text-amber-400' : status === 'tomorrow' ? 'text-blue-400' : 'text-gray-500'}`}>
                        <RepublicLogo />
                        <span className="flex-1">
                          {bulkSameDay ? 'Trash & Bulk' : 'Trash'} &mdash; <span className="font-semibold">{(property as any).trash_day}s</span>
                        </span>
                        {status === 'today' && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-full text-[10px] font-bold tracking-wide">
                            🗑 PUT OUT BINS
                          </span>
                        )}
                        {status === 'tomorrow' && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 border border-blue-500/40 text-blue-300 rounded-full text-[10px] font-bold tracking-wide">
                            🗑 TONIGHT
                          </span>
                        )}
                      </div>
                      {(property as any).bulk_day && !bulkSameDay && (() => {
                        const bulkStatus = getTrashStatus((property as any).bulk_day);
                        return (
                          <div className={`flex items-center gap-2 text-xs font-mono mt-1.5 ml-5 ${bulkStatus === 'today' ? 'text-amber-400' : bulkStatus === 'tomorrow' ? 'text-blue-400' : 'text-gray-600'}`}>
                            <span className="flex-1">Bulk &mdash; <span className="font-semibold">{(property as any).bulk_day}s</span> <span className="text-[10px] opacity-60">(biweekly)</span></span>
                            {bulkStatus === 'today' && <span className="px-2 py-0.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-full text-[10px] font-bold">PUT OUT BINS</span>}
                            {bulkStatus === 'tomorrow' && <span className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/40 text-blue-300 rounded-full text-[10px] font-bold">TONIGHT</span>}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Properties;
